import { Link } from "react-router-dom";
import { ShieldCheck, Clock3, FileSearch } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export const inputCls =
  "w-full rounded-xl border border-sand bg-white px-4 py-3 text-sm text-charcoal outline-none transition-colors placeholder:text-slate/50 focus:border-brass";

export const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
    <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z" />
    <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09C3.26 21.3 7.31 24 12 24z" />
    <path fill="#FBBC05" d="M5.27 14.29c-.25-.72-.38-1.49-.38-2.29s.14-1.57.38-2.29V6.62H1.29C.47 8.24 0 10.06 0 12s.47 3.76 1.29 5.38l3.98-3.09z" />
    <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z" />
  </svg>
);

export const GoogleButton = () => {
  const { googleLogin } = useAuth();
  return (
    <button
      type="button"
      data-testid="google-signin-button"
      onClick={googleLogin}
      className="flex w-full items-center justify-center gap-3 rounded-full border border-sand bg-white px-6 py-3 text-sm font-semibold text-charcoal transition-all duration-300 hover:-translate-y-0.5 hover:border-concordia/40 hover:shadow-[0_10px_24px_rgba(20,40,75,0.12)]"
    >
      <GoogleIcon />
      Continue with Google
    </button>
  );
};

const PERKS = [
  { icon: FileSearch, text: "Track every loan and card application in one place" },
  { icon: Clock3, text: "See live status — submitted, under process, decided" },
  { icon: ShieldCheck, text: "Your details stay encrypted and are never sold" },
];

export const AuthShell = ({ eyebrow, title, subtitle, children, footer }) => (
  <div data-testid="auth-shell" className="pb-24 pt-32">
    <div className="mx-auto grid max-w-6xl overflow-hidden rounded-3xl border border-sand bg-white shadow-[0_30px_80px_rgba(20,40,75,0.1)] lg:mx-8 lg:grid-cols-2 xl:mx-auto">
      <div className="relative hidden flex-col justify-between bg-concordia-ink p-10 text-ivory lg:flex">
        <div className="noise-overlay" aria-hidden="true" />
        <div className="relative">
          <p className="font-mono text-[0.62rem] uppercase tracking-[0.3em] text-brass">{eyebrow}</p>
          <h1 className="mt-5 font-serif text-4xl font-semibold leading-tight tracking-tight">{title}</h1>
          <p className="mt-4 max-w-sm text-sm leading-relaxed text-ivory/70">{subtitle}</p>
        </div>
        <ul className="relative mt-10 space-y-5">
          {PERKS.map((p) => (
            <li key={p.text} className="flex items-center gap-3 text-sm text-ivory/80">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-brass/40 text-brass">
                <p.icon className="h-4 w-4" />
              </span>
              {p.text}
            </li>
          ))}
        </ul>
      </div>
      <div className="p-6 sm:p-10">
        <div className="lg:hidden">
          <p className="font-mono text-[0.62rem] uppercase tracking-[0.3em] text-brass">{eyebrow}</p>
          <h1 className="mt-3 font-serif text-3xl font-semibold tracking-tight text-concordia">{title}</h1>
          <p className="mt-2 text-sm leading-relaxed text-slate">{subtitle}</p>
        </div>
        {children}
        {footer && <p className="mt-6 text-center text-xs text-slate">{footer}</p>}
      </div>
    </div>
    <p className="mx-auto mt-6 max-w-md px-4 text-center text-[0.7rem] leading-relaxed text-slate/70">
      Loan Salaah never asks for passwords, OTPs or card details over email or phone.{" "}
      <Link to="/legal/privacy-policy" className="underline decoration-brass/50 underline-offset-2">Privacy Policy</Link>
    </p>
  </div>
);
